import React, { useState, useRef, useEffect } from 'react';
import './YearDropdown.css';

const YearDropdown = ({ years, selectedYear, onChange, loading }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);

    // Filter years based on search
    const filteredYears = years.filter(year => 
        year.toString().includes(searchTerm)
    );

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Focus search input when dropdown opens
    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isOpen]);

    // Keyboard navigation
    const handleKeyDown = (e) => {
        if (!isOpen) {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsOpen(true);
            }
            return;
        }

        switch (e.key) {
            case 'Escape':
                setIsOpen(false);
                setSearchTerm('');
                break;
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev => 
                    prev < filteredYears.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && filteredYears[highlightedIndex]) {
                    handleYearSelect(filteredYears[highlightedIndex]);
                }
                break;
            default:
                break;
        }
    };

    const handleYearSelect = (year) => {
        onChange(Number(year));
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
    };

    if (loading) {
        return (
            <div className="year-dropdown-loading">
                <svg 
                    className="year-dropdown-spinner" 
                    width="20" 
                    height="20" 
                    viewBox="0 0 20 20" 
                    fill="none"
                >
                    <circle 
                        cx="10" 
                        cy="10" 
                        r="8" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round"
                        strokeDasharray="50"
                        strokeDashoffset="0"
                    />
                </svg>
                <span>Loading years...</span>
            </div>
        );
    }

    if (!years || years.length === 0) {
        return <div className="year-dropdown-empty">No years available</div>;
    }

    return (
        <div className="year-dropdown-container" ref={dropdownRef}>
            <button
                type="button"
                className="year-dropdown-trigger"
                onClick={() => setIsOpen(!isOpen)}
                onKeyDown={handleKeyDown}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
            >
                <span className="year-dropdown-value">{selectedYear || 'Select Year'}</span>
                <svg 
                    className={`year-dropdown-icon ${isOpen ? 'open' : ''}`}
                    width="20" 
                    height="20" 
                    viewBox="0 0 20 20" 
                    fill="none"
                >
                    <path 
                        d="M5 7.5L10 12.5L15 7.5" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                    />
                </svg>
            </button>

            {isOpen && (
                <div className="year-dropdown-menu">
                    <div className="year-dropdown-search">
                        <svg 
                            className="year-dropdown-search-icon" 
                            width="16" 
                            height="16" 
                            viewBox="0 0 16 16"
                            fill="none"
                        >
                            <path 
                                d="M7 13C10.3137 13 13 10.3137 13 7C13 3.68629 10.3137 1 7 1C3.68629 1 1 3.68629 1 7C1 10.3137 3.68629 13 7 13Z" 
                                stroke="currentColor" 
                                strokeWidth="2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                            />
                            <path 
                                d="M15 15L11.5 11.5" 
                                stroke="currentColor" 
                                strokeWidth="2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                            />
                        </svg>
                        <input
                            ref={searchInputRef}
                            type="text"
                            placeholder="Search year..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setHighlightedIndex(0);
                            }}
                            onKeyDown={handleKeyDown}
                            className="year-dropdown-search-input"
                        />
                    </div>

                    <ul className="year-dropdown-list" role="listbox">
                        {filteredYears.length > 0 ? (
                            filteredYears.map((year, index) => (
                                <li
                                    key={year}
                                    role="option"
                                    aria-selected={year === selectedYear}
                                    className={`year-dropdown-item ${
                                        year === selectedYear ? 'selected' : ''
                                    } ${index === highlightedIndex ? 'highlighted' : ''}`}
                                    onClick={() => handleYearSelect(year)}
                                    onMouseEnter={() => setHighlightedIndex(index)}
                                >
                                    <span>{year}</span>
                                    {year === selectedYear && (
                                        <svg 
                                            className="year-dropdown-check" 
                                            width="16" 
                                            height="16" 
                                            viewBox="0 0 16 16"
                                            fill="none"
                                        >
                                            <path 
                                                d="M13 4L6 11L3 8" 
                                                stroke="currentColor" 
                                                strokeWidth="2" 
                                                strokeLinecap="round" 
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    )}
                                </li>
                            ))
                        ) : (
                            <li className="year-dropdown-no-results">No years found</li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default YearDropdown;
